// src/utils/printUtils.ts
// DROP 42A — ERUE Festival Suite Print Layer
// Shared print engine for all receipt/document printing

// ── Core print engine ─────────────────────────────────────────────────────────
export function printHtml(html: string, title = 'ERUE Festival 2026'): void {
  const w = window.open('', '_blank', 'width=720,height=900,scrollbars=yes');
  if (!w) { alert('Please allow popups to print.'); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); setTimeout(() => w.close(), 800); }, 400);
}

// ── Shared ERUE letterhead ────────────────────────────────────────────────────
function erueHead(docTitle: string): string {
  return `
    <div class="letterhead">
      <div class="logo-row">
        <span class="festival-name">ERUE FESTIVAL 2026</span>
        <span class="kingdom">Uzairue Kingdom — Etsako West LGA, Edo State, Nigeria</span>
      </div>
      <div class="doc-title">${docTitle}</div>
      <div class="divider"></div>
    </div>`;
}

function erueFooter(): string {
  return `
    <div class="doc-footer">
      <div class="divider"></div>
      <p>Festival Dates: December 24–30, 2026 &nbsp;·&nbsp; Grand Finale: Wednesday 30 December 2026</p>
      <p>Palace of the Ogieneni of Uzairue Kingdom, Jattu-Uzairue</p>
      <p style="font-style:italic;color:#666">"Uzairue Heritage: Culture, Security, Prosperity"</p>
      <p>📞 +234 705 7840 320 &nbsp;·&nbsp; ✉️ erueuzairue@gmail.com</p>
      <p style="font-size:9px;color:#aaa">Printed: ${new Date().toLocaleString('en-NG')}</p>
    </div>`;
}

// ── Shared CSS ────────────────────────────────────────────────────────────────
function baseStyles(): string {
  return `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body {
        font-family: 'Segoe UI', Arial, sans-serif;
        background: #fff;
        color: #111;
        padding: 24px;
        max-width: 680px;
        margin: 0 auto;
        font-size: 13px;
        line-height: 1.6;
      }
      .letterhead { text-align: center; margin-bottom: 20px; }
      .logo-row { margin-bottom: 4px; }
      .festival-name {
        font-family: Georgia, serif;
        font-size: 22px;
        font-weight: 900;
        color: #0F5132;
        letter-spacing: 2px;
        display: block;
      }
      .kingdom {
        font-size: 11px;
        color: #555;
        letter-spacing: 0.5px;
        display: block;
        margin-top: 2px;
      }
      .doc-title {
        font-family: Georgia, serif;
        font-size: 14px;
        font-weight: 700;
        color: #C9A84C;
        text-transform: uppercase;
        letter-spacing: 2px;
        margin: 10px 0 8px;
      }
      .divider {
        border: none;
        border-top: 2px solid #C9A84C;
        margin: 8px 0;
      }
      .doc-footer {
        margin-top: 28px;
        text-align: center;
        font-size: 10px;
        color: #888;
      }
      .doc-footer p { margin: 3px 0; }
      .field-row {
        display: flex;
        justify-content: space-between;
        padding: 7px 0;
        border-bottom: 1px solid #eee;
      }
      .field-label { color: #555; font-size: 11px; text-transform: uppercase; }
      .field-value { font-weight: 600; color: #111; text-align: right; }
      .highlight-box {
        background: #f5f0e0;
        border: 2px solid #C9A84C;
        border-radius: 8px;
        padding: 14px 18px;
        margin: 16px 0;
        text-align: center;
      }
      .highlight-id {
        font-family: 'Courier New', monospace;
        font-size: 18px;
        font-weight: 900;
        color: #0F5132;
        letter-spacing: 3px;
      }
      .section-title {
        font-family: Georgia, serif;
        font-size: 12px;
        font-weight: 700;
        color: #0F5132;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin: 16px 0 8px;
        border-left: 3px solid #C9A84C;
        padding-left: 8px;
      }
      table { width: 100%; border-collapse: collapse; margin: 8px 0; }
      th {
        background: #0F5132;
        color: #fff;
        padding: 7px 10px;
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      td { padding: 7px 10px; border-bottom: 1px solid #eee; font-size: 12px; }
      tr:nth-child(even) td { background: #f9f9f9; }
      .total-row td {
        font-weight: 700;
        background: #f5f0e0 !important;
        border-top: 2px solid #C9A84C;
        font-size: 13px;
      }
      .status-badge {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 20px;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      .status-success { background: #e6f9ee; color: #0a5c2e; border: 1px solid #0F5132; }
      .status-pending { background: #fff8e6; color: #7a5200; border: 1px solid #C9A84C; }
      .notice-box {
        background: #e6f9ee;
        border: 1px solid #0F5132;
        border-radius: 6px;
        padding: 10px 14px;
        margin: 14px 0;
        font-size: 12px;
        color: #0a5c2e;
      }
      .variance-over { color: #c0392b; font-weight: 700; }
      .variance-under { color: #0a5c2e; font-weight: 700; }
      @media print {
        body { padding: 0; }
        .no-print { display: none !important; }
      }
    </style>`;
}

function docWrapper(body: string): string {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    ${baseStyles()}
  </head><body>${body}</body></html>`;
}

// ── 1. Registration Slip ──────────────────────────────────────────────────────
export interface RegistrationSlipData {
  id?: string;
  type: string;
  name?: string;
  email?: string;
  phone?: string;
  village?: string;
  state?: string;
  status?: string;
  submittedAt?: string;
  extraFields?: Record<string, string>;
}

export function printRegistrationSlip(data: RegistrationSlipData): void {
  const typeLabel = data.type === 'miss-erue'    ? '👑 Miss ERUE Contestant'
                 : data.type === 'general'        ? '🎉 General Attendee'
                 : data.type === 'vip'            ? '⭐ VIP Guest'
                 : data.type === 'vendor'         ? '🏪 Vendor'
                 : data.type === 'volunteer'      ? '🤝 Volunteer'
                 : data.type;

  const extras = data.extraFields
    ? Object.entries(data.extraFields)
        .filter(([, v]) => v)
        .map(([k, v]) => `
          <div class="field-row">
            <span class="field-label">${k.replace(/_/g, ' ')}</span>
            <span class="field-value">${v}</span>
          </div>`).join('')
    : '';

  const body = `
    ${erueHead('Festival Registration Confirmation')}
    <div class="notice-box">
      ✅ Your registration has been received and is pending admin review.
      You will be notified once approved.
    </div>
    ${data.id ? `
    <div class="highlight-box">
      <div style="font-size:10px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">Registration ID</div>
      <div class="highlight-id">${data.id}</div>
      <div style="font-size:10px;color:#888;margin-top:6px">Keep this number for reference</div>
    </div>` : ''}
    <div class="section-title">Registration Details</div>
    <div class="field-row">
      <span class="field-label">Registration Type</span>
      <span class="field-value">${typeLabel}</span>
    </div>
    ${data.name ? `<div class="field-row"><span class="field-label">Full Name</span><span class="field-value">${data.name}</span></div>` : ''}
    ${data.email ? `<div class="field-row"><span class="field-label">Email</span><span class="field-value">${data.email}</span></div>` : ''}
    ${data.phone ? `<div class="field-row"><span class="field-label">Phone</span><span class="field-value">${data.phone}</span></div>` : ''}
    ${data.village ? `<div class="field-row"><span class="field-label">Village / Community</span><span class="field-value">${data.village}</span></div>` : ''}
    ${data.state ? `<div class="field-row"><span class="field-label">State of Origin</span><span class="field-value">${data.state}</span></div>` : ''}
    ${extras}
    <div class="field-row">
      <span class="field-label">Status</span>
      <span class="field-value">
        <span class="status-badge status-pending">Pending Review</span>
      </span>
    </div>
    ${data.submittedAt ? `<div class="field-row"><span class="field-label">Submitted</span><span class="field-value">${data.submittedAt}</span></div>` : ''}
    ${erueFooter()}`;

  printHtml(docWrapper(body), 'ERUE Registration Slip');
}

// ── 2. Vote Receipt ───────────────────────────────────────────────────────────
export interface VoteReceiptData {
  contestantName: string;
  contestantVillage?: string;
  voterEmail: string;
  voterName?: string;
  bundleLabel: string;
  votes: number;
  amountPaid: number;
  reference?: string;
  timestamp?: string;
}

export function printVoteReceipt(data: VoteReceiptData): void {
  const fmt = (n: number) => '₦' + n.toLocaleString('en-NG');
  const body = `
    ${erueHead('Miss ERUE Voting Receipt')}
    <div class="notice-box">
      ✅ Your votes have been credited successfully. Thank you for participating!
    </div>
    <div class="highlight-box">
      <div style="font-size:11px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">You Voted For</div>
      <div style="font-family:Georgia,serif;font-size:20px;font-weight:900;color:#0F5132">${data.contestantName}</div>
      ${data.contestantVillage ? `<div style="font-size:11px;color:#888;margin-top:4px">${data.contestantVillage}</div>` : ''}
      <div style="font-size:28px;font-weight:900;color:#C9A84C;margin-top:10px">🗳️ ${data.votes.toLocaleString()} Votes</div>
    </div>
    <div class="section-title">Payment Details</div>
    <div class="field-row">
      <span class="field-label">Vote Bundle</span>
      <span class="field-value">${data.bundleLabel}</span>
    </div>
    <div class="field-row">
      <span class="field-label">Amount Paid</span>
      <span class="field-value">${fmt(data.amountPaid)}</span>
    </div>
    ${data.voterEmail ? `<div class="field-row"><span class="field-label">Voter Email</span><span class="field-value">${data.voterEmail}</span></div>` : ''}
    ${data.voterName ? `<div class="field-row"><span class="field-label">Voter Name</span><span class="field-value">${data.voterName}</span></div>` : ''}
    ${data.reference ? `<div class="field-row"><span class="field-label">Reference</span><span class="field-value" style="font-family:monospace">${data.reference}</span></div>` : ''}
    <div class="field-row">
      <span class="field-label">Status</span>
      <span class="field-value"><span class="status-badge status-success">Votes Credited</span></span>
    </div>
    ${data.timestamp ? `<div class="field-row"><span class="field-label">Date/Time</span><span class="field-value">${data.timestamp}</span></div>` : ''}
    ${erueFooter()}`;

  printHtml(docWrapper(body), 'ERUE Vote Receipt');
}

// ── 3. Store Order Receipt ────────────────────────────────────────────────────
export interface OrderReceiptItem {
  name: string;
  quantity: number;
  price: number;
}

export interface OrderReceiptData {
  reference: string;
  name: string;
  email: string;
  phone?: string;
  amount: number;
  items: OrderReceiptItem[];
  purchasedAt: string;
  status: string;
  paymentChannel?: string;
}

export function printOrderReceipt(data: OrderReceiptData): void {
  const fmt = (n: number) => '₦' + n.toLocaleString('en-NG');
  const rows = data.items.map((item, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${item.name || 'Item'}</td>
      <td style="text-align:center">${item.quantity || 1}</td>
      <td style="text-align:right">${fmt(item.price || 0)}</td>
      <td style="text-align:right">${fmt((item.price || 0) * (item.quantity || 1))}</td>
    </tr>`).join('');

  const body = `
    ${erueHead('Festival Store — Order Receipt')}
    <div class="notice-box">✅ Payment confirmed. Thank you for your purchase!</div>
    <div class="highlight-box">
      <div style="font-size:10px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">Order Reference</div>
      <div class="highlight-id" style="font-size:14px">${data.reference}</div>
    </div>
    <div class="section-title">Customer Details</div>
    <div class="field-row"><span class="field-label">Name</span><span class="field-value">${data.name}</span></div>
    <div class="field-row"><span class="field-label">Email</span><span class="field-value">${data.email}</span></div>
    ${data.phone ? `<div class="field-row"><span class="field-label">Phone</span><span class="field-value">${data.phone}</span></div>` : ''}
    <div class="field-row"><span class="field-label">Date</span><span class="field-value">${data.purchasedAt}</span></div>
    ${data.paymentChannel ? `<div class="field-row"><span class="field-label">Payment Via</span><span class="field-value">${data.paymentChannel}</span></div>` : ''}
    <div class="field-row">
      <span class="field-label">Status</span>
      <span class="field-value"><span class="status-badge status-success">${data.status}</span></span>
    </div>
    <div class="section-title">Order Items</div>
    <table>
      <thead><tr><th>#</th><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>
        ${rows}
        <tr class="total-row">
          <td colspan="4" style="text-align:right">TOTAL PAID</td>
          <td style="text-align:right">${fmt(data.amount)}</td>
        </tr>
      </tbody>
    </table>
    ${erueFooter()}`;

  printHtml(docWrapper(body), 'ERUE Store Receipt');
}

// ── 4. Budget Plan Print ──────────────────────────────────────────────────────
export interface BudgetLineItem {
  category: string;
  description?: string;
  activityLevel?: string;
  estimatedAmount: number;
  budgeted: number;
  actual: number;
}

export interface BudgetRevSource {
  source: string;
  estimatedAmount: number;
  received: number;
}

export interface BudgetPrintData {
  title: string;
  level: string;
  status: string;
  totalBudgeted: number;
  totalActual: number;
  totalEstimatedExpenditure: number;
  totalEstimatedRevenue: number;
  lineItems: BudgetLineItem[];
  revenueSources: BudgetRevSource[];
  createdBy?: string;
  createdAt?: string;
  notes?: string;
}

export function printBudgetPlan(data: BudgetPrintData): void {
  const fmt = (n: number) => '₦' + (n || 0).toLocaleString('en-NG');
  const surplus = data.totalEstimatedRevenue - data.totalEstimatedExpenditure;

  const itemRows = data.lineItems.map((item, i) => {
    const v = item.budgeted - item.actual;
    return `<tr>
      <td>${i + 1}</td>
      <td>${item.category}</td>
      <td style="font-size:10px;color:#555">${item.activityLevel || 'L2'}</td>
      <td style="text-align:right">${fmt(item.estimatedAmount)}</td>
      <td style="text-align:right">${fmt(item.budgeted)}</td>
      <td style="text-align:right">${fmt(item.actual)}</td>
      <td style="text-align:right" class="${v >= 0 ? 'variance-under' : 'variance-over'}">${v >= 0 ? '+' : ''}${fmt(v)}</td>
    </tr>`;}).join('');

  const revRows = data.revenueSources.map((r, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${r.source}</td>
      <td style="text-align:right">${fmt(r.estimatedAmount)}</td>
      <td style="text-align:right">${fmt(r.received)}</td>
    </tr>`).join('');

  const body = `
    ${erueHead('Budget Plan — ' + data.title)}
    <div class="section-title">Plan Summary</div>
    <div class="field-row"><span class="field-label">Budget Level</span><span class="field-value">${data.level.toUpperCase()}</span></div>
    <div class="field-row"><span class="field-label">Status</span><span class="field-value"><span class="status-badge status-pending">${data.status.replace('_', ' ').toUpperCase()}</span></span></div>
    <div class="field-row"><span class="field-label">Est. Expenditure</span><span class="field-value">${fmt(data.totalEstimatedExpenditure)}</span></div>
    <div class="field-row"><span class="field-label">Total Budgeted</span><span class="field-value">${fmt(data.totalBudgeted)}</span></div>
    <div class="field-row"><span class="field-label">Actual Spend</span><span class="field-value">${fmt(data.totalActual)}</span></div>
    <div class="field-row"><span class="field-label">Est. Revenue</span><span class="field-value">${fmt(data.totalEstimatedRevenue)}</span></div>
    <div class="field-row">
      <span class="field-label">Projected Surplus/Deficit</span>
      <span class="field-value ${surplus >= 0 ? 'variance-under' : 'variance-over'}">${surplus >= 0 ? '+' : ''}${fmt(surplus)}</span>
    </div>
    ${data.notes ? `<div class="field-row"><span class="field-label">Notes</span><span class="field-value">${data.notes}</span></div>` : ''}
    ${data.createdBy ? `<div class="field-row"><span class="field-label">Created By</span><span class="field-value">${data.createdBy}</span></div>` : ''}
    ${data.createdAt ? `<div class="field-row"><span class="field-label">Created</span><span class="field-value">${new Date(data.createdAt).toLocaleDateString('en-NG')}</span></div>` : ''}

    <div class="section-title">Expenditure Line Items</div>
    <table>
      <thead><tr><th>#</th><th>Category</th><th>Level</th><th style="text-align:right">Estimated</th><th style="text-align:right">Budgeted</th><th style="text-align:right">Actual</th><th style="text-align:right">Variance</th></tr></thead>
      <tbody>
        ${itemRows}
        <tr class="total-row">
          <td colspan="3">TOTAL</td>
          <td style="text-align:right">${fmt(data.totalEstimatedExpenditure)}</td>
          <td style="text-align:right">${fmt(data.totalBudgeted)}</td>
          <td style="text-align:right">${fmt(data.totalActual)}</td>
          <td style="text-align:right" class="${data.totalBudgeted - data.totalActual >= 0 ? 'variance-under' : 'variance-over'}">${data.totalBudgeted - data.totalActual >= 0 ? '+' : ''}${fmt(data.totalBudgeted - data.totalActual)}</td>
        </tr>
      </tbody>
    </table>

    ${data.revenueSources.length > 0 ? `
    <div class="section-title">Projected Revenue Sources</div>
    <table>
      <thead><tr><th>#</th><th>Source</th><th style="text-align:right">Estimated</th><th style="text-align:right">Received</th></tr></thead>
      <tbody>
        ${revRows}
        <tr class="total-row">
          <td colspan="2">TOTAL</td>
          <td style="text-align:right">${fmt(data.totalEstimatedRevenue)}</td>
          <td style="text-align:right">${fmt(data.revenueSources.reduce((s, r) => s + (r.received || 0), 0))}</td>
        </tr>
      </tbody>
    </table>` : ''}
    ${erueFooter()}`;

  printHtml(docWrapper(body), 'ERUE Budget Plan');
}

// ── 5. Variance Report Print ──────────────────────────────────────────────────
export interface VarianceRow {
  category: string;
  estimated: number;
  budgeted: number;
  actual: number;
}

export function printVarianceReport(rows: VarianceRow[], totals: { estimated: number; budgeted: number; actual: number }): void {
  const fmt = (n: number) => '₦' + (n || 0).toLocaleString('en-NG');
  const tableRows = rows.map((r, i) => {
    const v = r.budgeted - r.actual;
    return `<tr>
      <td>${i + 1}</td>
      <td>${r.category}</td>
      <td style="text-align:right">${fmt(r.estimated)}</td>
      <td style="text-align:right">${fmt(r.budgeted)}</td>
      <td style="text-align:right">${fmt(r.actual)}</td>
      <td style="text-align:right" class="${v >= 0 ? 'variance-under' : 'variance-over'}">${v >= 0 ? '+' : ''}${fmt(v)}</td>
      <td style="text-align:right">${r.budgeted > 0 ? ((r.actual / r.budgeted) * 100).toFixed(1) + '%' : '—'}</td>
    </tr>`;}).join('');
  const tv = totals.budgeted - totals.actual;

  const body = `
    ${erueHead('Budget Variance Report — All Plans')}
    <div class="field-row"><span class="field-label">Report Date</span><span class="field-value">${new Date().toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
    <div class="field-row"><span class="field-label">Total Estimated</span><span class="field-value">${fmt(totals.estimated)}</span></div>
    <div class="field-row"><span class="field-label">Total Budgeted</span><span class="field-value">${fmt(totals.budgeted)}</span></div>
    <div class="field-row"><span class="field-label">Total Actual</span><span class="field-value">${fmt(totals.actual)}</span></div>
    <div class="field-row">
      <span class="field-label">Net Variance</span>
      <span class="field-value ${tv >= 0 ? 'variance-under' : 'variance-over'}">${tv >= 0 ? '+' : ''}${fmt(tv)}</span>
    </div>
    <div class="section-title">Variance by Category</div>
    <table>
      <thead><tr><th>#</th><th>Category</th><th style="text-align:right">Estimated</th><th style="text-align:right">Budgeted</th><th style="text-align:right">Actual</th><th style="text-align:right">Variance</th><th style="text-align:right">% Used</th></tr></thead>
      <tbody>
        ${tableRows}
        <tr class="total-row">
          <td colspan="2">TOTAL</td>
          <td style="text-align:right">${fmt(totals.estimated)}</td>
          <td style="text-align:right">${fmt(totals.budgeted)}</td>
          <td style="text-align:right">${fmt(totals.actual)}</td>
          <td style="text-align:right" class="${tv >= 0 ? 'variance-under' : 'variance-over'}">${tv >= 0 ? '+' : ''}${fmt(tv)}</td>
          <td style="text-align:right">${totals.budgeted > 0 ? ((totals.actual / totals.budgeted) * 100).toFixed(1) + '%' : '—'}</td>
        </tr>
      </tbody>
    </table>
    ${erueFooter()}`;

  printHtml(docWrapper(body), 'ERUE Variance Report');
}
